import { useEffect, useState } from "react";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import layoutConf from "../../config/layoutConf";
import { useTranslation } from "react-i18next";
import AutoForm from "../form/functional/AutoForm";
import getSchema from "../../lib/api/getSchema";
import { debounce, cloneDeep } from "lodash";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import BasicCrudScreenHeaderLayout from "../screenTypes/layout/BasicCrudScreenHeaderLayout";
import BasicCrudActionsLayout from "../screenTypes/layout/BasicCrudActionsLayout";
import ToolbarFlexSpacer from "../layout/ToolbarFlexSpacer";
import useNotification from "../../hooks/useNotification";
import CompanyCreateButton from "../input/CompanyCreateButton";
import CompanyUpdateButton from "../input/CompanyUpdateButton";
import CompanyDeleteButton from "../input/CompanyDeleteButton";
import AutoFormViewLayout from "../form/layout/AutoFormViewLayout";

const id = "MomSysUdtCompanyKonam";
const table = "MomSysUdtCompany";
const view = "MomSysVwCompany";

function hyphenNumericCond() {
    return {
        msg: "error.notHyphenOrNumeric",
        type: "regexp",
        value: "^[0-9-]+$",
    };
}

function emailCond() {
    return {
        msg: "error.emailRequired",
        type: "regexp",
        value: "^[a-zA-Z0-9+-\\_.]+@[a-zA-Z0-9-]+\\.[a-zA-Z0-9-.]+$",
    };
}

function tableModifySchema(schema, type = "create") {
    schema.formGroups.push("form.companyForm.groupName.sales");
    for (const column of schema.columns) {
        column.hideInForm = false;
        switch (column.name) {
            case "companyCode":
                if (type === "create") {
                    delete column.key;
                    column.readonly = false;
                }
                column.formOrder = 5;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "companyName":
                column.formOrder = 10;
                column.formGroup = 0;
                column.required = true;
                column.formItemType = "text";
                break;

            case "companyGrp":
                column.formOrder = 20;
                column.formGroup = 0;
                column.formItemType = "commoncode";
                column.commonCodeInfo = {
                    mstCodeId: "B009",
                    tableName: "CommonCodeSlave",
                    nameColumn: ["slaveCodeName"],
                    valueColumn: "slaveCodeId",
                    useEmptyValue: true,
                    emptyName: "term.none",
                    sortByName: true,
                    overrideLabel: "columnName.companyGrpName",
                };
                break;

            case "companyFullName":
                column.formOrder = 30;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "companyType":
                column.formGroup = 0;
                column.formOrder = 40;
                column.formItemType = "commoncode";
                column.commonCodeInfo = {
                    mstCodeId: "B005",
                    tableName: "CommonCodeSlave",
                    nameColumn: ["slaveCodeName"],
                    valueColumn: "slaveCodeId",
                    useEmptyValue: true,
                    emptyName: "term.none",
                    sortByName: true,
                    overrideLabel: "columnName.companyTypeName",
                };
                break;

            case "companyEngName":
                column.formOrder = 50;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "companyRegNo":
                column.formOrder = 60;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "corpRegNo":
                column.formOrder = 70;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "ceoName":
                column.formOrder = 80;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "foundingDate":
                column.formOrder = 90;
                column.formGroup = 0;
                column.formItemType = "date";
                break;

            case "businessType":
                column.formOrder = 100;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "industry":
                column.formOrder = 110;
                column.formGroup = 0;
                column.formItemType = "text";
                break;

            case "useFlag":
                column.formOrder = 120;
                column.formGroup = 0;
                column.formItemType = "checkbox";
                column.defaultValue = true;
                break;

            case "zipcode":
                column.formOrder = 10;
                column.formGroup = 1;
                column.formItemType = "zipcode";
                break;

            case "englishAddress":
                column.formOrder = 20;
                column.formGroup = 1;
                column.formItemType = "text";
                column.disabled = true;
                break;

            case "address":
                column.formOrder = 30;
                column.formGroup = 1;
                column.formItemType = "text";
                column.disabled = true;
                break;

            case "englishAddressDetail1":
                column.formOrder = 40;
                column.formGroup = 1;
                column.formItemType = "text";
                break;

            case "addressDetail":
                column.formOrder = 50;
                column.formGroup = 1;
                column.formItemType = "text";
                break;

            case "englishAddressDetail2":
                column.formOrder = 60;
                column.formGroup = 1;
                column.formItemType = "text";
                break;

            case "countryCode":
                column.formOrder = 70;
                column.formGroup = 1;
                column.formItemType = "commoncode";
                column.commonCodeInfo = {
                    mstCodeId: "B006",
                    tableName: "CommonCodeSlave",
                    nameColumn: ["slaveCodeId", "slaveCodeName"],
                    valueColumn: "slaveCodeId",
                    useEmptyValue: true,
                    emptyName: "term.none",
                    sortByName: true,
                    overrideLabel: "columnName.countryCodeName",
                };
                break;

            case "areaCode":
                column.formOrder = 80;
                column.formGroup = 1;
                column.formItemType = "commoncode";
                column.commonCodeInfo = {
                    mstCodeId: "B010",
                    tableName: "CommonCodeSlave",
                    nameColumn: ["slaveCodeId", "slaveCodeName"],
                    valueColumn: "slaveCodeId",
                    useEmptyValue: true,
                    emptyName: "term.none",
                    sortByName: true,
                    overrideLabel: "columnName.areaCodeName",
                };
                break;

            case "homePage":
                column.formOrder = 10;
                column.formGroup = 2;
                break;

            case "fax":
                column.formOrder = 20;
                column.formGroup = 2;
                break;

            case "phoneNumber1":
                column.formOrder = 30;
                column.formGroup = 2;
                column.validation = hyphenNumericCond();
                break;

            case "phoneNumber2":
                column.formOrder = 40;
                column.formGroup = 2;
                column.validation = hyphenNumericCond();
                break;

            case "salesPerson":
                column.formOrder = 10;
                column.formGroup = 3;
                column.displayName = "salesPersonName";
                break;

            case "salesPersonDept":
                column.formOrder = 20;
                column.formGroup = 3;
                break;

            case "salesPersonPosition":
                column.formOrder = 30;
                column.formGroup = 3;
                break;

            case "salesPersonEmail":
                column.formOrder = 40;
                column.formGroup = 3;
                column.displayName = "salesPersonEmailCharged";
                column.validation = emailCond();
                break;

            case "salesPersonPhone":
                column.formOrder = 50;
                column.formGroup = 3;
                column.displayName = "salesPersonPhoneCharged";
                column.validation = hyphenNumericCond();
                break;

            case "createdAt":
                column.useCurrentDateOnInsert = true;
                column.hideInForm = true;
                break;

            case "createdBy":
                column.useAuthProfileUsernameOnInsert = true;
                column.hideInForm = true;
                break;

            case "updatedAt":
                column.useCurrentDateOnUpdate = true;
                column.hideInForm = true;
                break;

            case "updatedBy":
                column.useAuthProfileUsernameOnUpdate = true;
                column.hideInForm = true;
                break;

            default:
                column.hideInForm = true;
                break;
        }
    }
}

function readOnlyModifySchema(schema) {
    schema.formGroups.push("form.companyForm.groupName.sales");

    schema.columns.push({ name: "companyTypeName" });
    schema.columns.push({ name: "countryCodeName" });
    schema.columns.push({ name: "areaCodeName" });
    schema.columns.push({ name: "companyGrpName" });

    for (const column of schema.columns) {
        column.disabled = true;
        column.hideInForm = true;
        switch (column.name) {
            case "companyGrpName":
                column.type = "varchar";
                column.formGroup = 0;
                column.formOrder = 10;
                column.hideInForm = false;
                break;

            case "companyTypeName":
                column.type = "varchar";
                column.formGroup = 0;
                column.formOrder = 15;
                column.hideInForm = false;
                break;

            case "companyFullName":
                column.formGroup = 0;
                column.formOrder = 20;
                column.hideInForm = false;
                break;

            case "corpRegNo":
                column.formGroup = 0;
                column.formOrder = 30;
                column.hideInForm = false;
                break;

            case "companyEngName":
                column.formGroup = 0;
                column.formOrder = 40;
                column.hideInForm = false;
                break;

            case "companyRegNo":
                column.formGroup = 0;
                column.formOrder = 50;
                column.hideInForm = false;
                break;

            case "ceoName":
                column.formGroup = 0;
                column.formOrder = 60;
                column.hideInForm = false;
                break;

            case "foundingDate":
                column.formGroup = 0;
                column.formOrder = 70;
                column.formItemType = "date";
                column.hideInForm = false;
                break;

            case "businessType":
                column.formGroup = 0;
                column.formOrder = 80;
                column.hideInForm = false;
                break;

            case "industry":
                column.formGroup = 0;
                column.formOrder = 90;
                column.hideInForm = false;
                break;

            case "useFlag":
                column.formGroup = 0;
                column.formOrder = 100;
                column.formItemType = "checkbox";
                column.hideInForm = false;
                break;

            case "countryCodeName":
                column.type = "varchar";
                column.formGroup = 1;
                column.formOrder = 20;
                column.hideInForm = false;
                break;

            case "areaCodeName":
                column.type = "varchar";
                column.formGroup = 1;
                column.formOrder = 30;
                column.hideInForm = false;
                break;

            case "zipcode":
                column.formGroup = 1;
                column.formOrder = 40;
                column.formItemType = "zipcode";
                column.hideInForm = false;
                break;

            case "englishAddress":
                column.formGroup = 1;
                column.formOrder = 50;
                column.hideInForm = false;
                column.disabled = true;
                break;

            case "address":
                column.formGroup = 1;
                column.formOrder = 60;
                column.hideInForm = false;
                column.disabled = true;
                break;

            case "englishAddressDetail1":
                column.formGroup = 1;
                column.formOrder = 70;
                column.hideInForm = false;
                break;

            case "addressDetail":
                column.formGroup = 1;
                column.formOrder = 80;
                column.hideInForm = false;
                break;

            case "englishAddressDetail2":
                column.formGroup = 1;
                column.formOrder = 90;
                column.hideInForm = false;
                break;

            case "homePage":
                column.formGroup = 2;
                column.formOrder = 10;
                column.hideInForm = false;
                break;

            case "fax":
                column.formGroup = 2;
                column.formOrder = 20;
                column.hideInForm = false;
                break;

            case "phoneNumber1":
                column.formGroup = 2;
                column.formOrder = 30;
                column.hideInForm = false;
                break;

            case "phoneNumber2":
                column.formGroup = 2;
                column.formOrder = 40;
                column.hideInForm = false;
                break;

            case "salesPerson":
                column.formGroup = 3;
                column.formOrder = 10;
                column.displayName = "salesPersonName";
                column.hideInForm = false;
                break;

            case "salesPersonDept":
                column.formGroup = 3;
                column.formOrder = 20;
                column.hideInForm = false;
                break;

            case "salesPersonPosition":
                column.formGroup = 3;
                column.formOrder = 30;
                column.hideInForm = false;
                break;

            case "salesPersonEmail":
                column.formGroup = 3;
                column.formOrder = 40;
                column.displayName = "salesPersonEmailCharged";
                column.hideInForm = false;
                break;

            case "salesPersonPhone":
                column.formGroup = 3;
                column.formOrder = 50;
                column.displayName = "salesPersonPhoneCharged";
                column.hideInForm = false;
                break;

            default:
                column.disabled = true;
                column.required = false;
                break;
        }
    }
}

/**
 * @COMPONENT_NAME MomSysUdtCompanyKONAM
 * @DESCRIPTION KONAM 테넌트용 회사 관리 화면 컴포넌트. 회사 데이터의 CRUD 작업과 폼 검증, 그리드 표시를 제공합니다.
 *
 * @PROPS
 *    - {Object} props - 컴포넌트 props (현재 미사용)
 *
 * @PASSTHROUGH_PROPS
 *    - {string} id - 화면 식별자
 *    - {string} table - 데이터베이스 테이블명
 *    - {string} view - 데이터베이스 뷰명
 *
 * @CALLBACKS
 *    - {Function} onSuccess - CRUD 작업 성공 시 호출
 *    - {Function} onFailure - CRUD 작업 실패 시 호출
 *    - {Function} onNotifyWarning - 경고 알림 시 호출
 *
 * @EXAMPLES
 * ```jsx
 * <MomSysUdtCompanyKONAM />
 * ```
 */
function MomSysUdtCompanyKONAM(props) {
    const { t } = useTranslation();
    const { notifySuccess, notifyFailure, notifyWarning } = useNotification();

    const [api, setApi] = useState();

    const [readOnlyTableSchema, setReadOnlyTableSchema] = useState();
    const [createTableSchema, setCreateTableSchema] = useState();
    const [commonTableSchema, setCommonTableSchema] = useState();

    const [grid, setGrid] = useState();
    const [handleSelect, setHandleSelect] = useState();
    const [isEmpty, setIsEmpty] = useState();

    useEffect(() => {
        let mounted = true;

        window.addEventListener("resize", handleResize);
        handleResize();

        (async () => {
            let schema = await getSchema(table);

            let createTableSchema = cloneDeep(schema);
            tableModifySchema(createTableSchema, "create");
            let commonTableSchema = cloneDeep(schema);
            tableModifySchema(commonTableSchema, "update");
            let readOnlySchema = cloneDeep(schema);
            readOnlyModifySchema(readOnlySchema);

            if (mounted) {
                setReadOnlyTableSchema(readOnlySchema);
                setCreateTableSchema(createTableSchema);
                setCommonTableSchema(commonTableSchema);
            }
        })();

        return () => {
            mounted = false;
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (grid) {
            grid.selectionChanged.addHandler(
                debounce((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        setHandleSelect(grid.selectedItems[0]);
                    } else {
                        setHandleSelect([]);
                    }
                }, 10)
            );

            grid.itemsSourceChanged.addHandler(
                debounce((grid, event) => {
                    if (grid.selectedItems && grid.selectedItems[0]) {
                        setHandleSelect(grid.selectedItems[0]);
                    } else {
                        setHandleSelect([]);
                    }
                }, 10)
            );
        }
    }, [grid]);

    const [height, setHeight] = useState(0);
    const handleResize = () => {
        setHeight(document.documentElement.offsetHeight - 96);
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
    };

    const onFailure = (reason) => {
        notifyFailure(reason.response ? reason.response.data : reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
    };

    return (
        <div style={{ height: height }}>
            <ReflexContainer orientation="vertical">
                <ReflexElement flex={0.25}>
                    <BasicCrudScreenType
                        id={id}
                        table={table}
                        view={view}
                        headerTitle={t("tableName.Vendor")}
                        onInitialized={(grid) => setGrid(grid)}
                        onApiInitialized={(api) => setApi(api)}
                        onLoadData={(data) => {
                            setIsEmpty(data.length < 1 ? true : false);
                        }}
                        noCreate
                        noEdit
                        noDelete
                        noExcel
                    />
                </ReflexElement>
                <ReflexSplitter style={{ width: layoutConf.reflex.splitterWidth }} />
                <ReflexElement flex={0.75}>
                    <BasicCrudScreenHeaderLayout className="basicCrudScreenHeaderLayout">
                        <BasicCrudActionsLayout>
                            <ToolbarFlexSpacer xs />
                            <BasicCrudScreenActionItemLayout>
                                <CompanyCreateButton
                                    id={id}
                                    schema={createTableSchema}
                                    refresh={api}
                                    onSuccess={onSuccess}
                                />
                            </BasicCrudScreenActionItemLayout>
                            <BasicCrudScreenActionItemLayout>
                                <CompanyUpdateButton
                                    id={id}
                                    schema={commonTableSchema}
                                    grid={grid}
                                    refresh={api}
                                    onSuccess={onSuccess}
                                    disabled={isEmpty}
                                />
                            </BasicCrudScreenActionItemLayout>
                            <BasicCrudScreenActionItemLayout>
                                <CompanyDeleteButton
                                    id={id}
                                    schema={commonTableSchema}
                                    grid={grid}
                                    color="secondary"
                                    refresh={api}
                                    onSuccess={onSuccess}
                                    onFailure={onFailure}
                                    onPartialFailure={onNotifyWarning}
                                    disabled={isEmpty}
                                />
                            </BasicCrudScreenActionItemLayout>
                        </BasicCrudActionsLayout>
                        <AutoFormViewLayout>
                            <AutoForm
                                schema={readOnlyTableSchema}
                                mode="edit"
                                initialData={{ ...handleSelect }}
                                onChange={() => {}}
                            />
                        </AutoFormViewLayout>
                    </BasicCrudScreenHeaderLayout>
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
}

export default MomSysUdtCompanyKONAM;
